import ContinueButton from "@components/ContinueButton";
import Epic from "@components/Epic";
import Error from "@hudson/ui/components/Error";
import Field from "@hudson/ui/components/FormFields/Field";
import Input from "@hudson/ui/components/FormFields/Input";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import { FormProvider, useForm } from "@hudson/ui/components/ReactHookForm";
import addEpicPass from "@lib/services/add_epic_pass";
import epicPassValidate from "@lib/services/epic_pass_validate";
import Session from "@lib/session";
import useContent from "@lib/use_content";
import { useState } from "react";
import epicMountainRewards from "./epic_mountain_rewards.png";

interface LoginProps {
  className?: string;
  onSuccessfulLogin?: (profileType?: string, successMsg?: string) => void;
}

export default function Login({ className = "", onSuccessfulLogin }: LoginProps) {
  const { data: contentData, loading } = useContent([
    "beErrFieldRequired",
    "beErrEpicLogin",
    "beEpicPassAppliedMsg",
    "beEpicPassIdLabel",
    "beEpicPassSubmitBtn",
  ]);
  const formMethods = useForm();

  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  interface LoginData {
    code: string;
  }

  async function submitForm(data: LoginData) {
    setErrorMessage("");
    setSubmitting(true);

    // Login epic pass id
    const success = await epicPassValidate({ id: data.code });

    if (!success) {
      setErrorMessage(contentData?.content.beErrEpicLogin);
      setSubmitting(false);
    } else {
      await addEpicPass({ id: data.code });
      if (onSuccessfulLogin) onSuccessfulLogin();
    }
  }

  return loading ? (
    <ConcentricLoader className="max-w-2xl px-4 mx-auto opacity-60 sm:py-10 sm:px-6 lg:px-8" />
  ) : (
    <FormProvider {...formMethods}>
      <form
        className="flex flex-col items-center pt-10 mx-auto gap-y-6 sm:gap-x-4 max-w-base"
        id="login"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          formMethods.handleSubmit(submitForm)();
        }}
        noValidate
      >
        <div className={`${className} `}>
          {Session.getBooking().otherInfo.epicPassId ? (
            <Epic className="mt-4" />
          ) : (
            <>
              <img
                src={epicMountainRewards.src}
                key="test"
                alt="Epic Mountain Rewards"
                className="h-12 mb-4"
              />

              {errorMessage && (
                <div className="relative">
                  <Error className="mt-6 mb-6" message={errorMessage} heavyStyle scrollTo />
                </div>
              )}

              <div className="flex justify-center gap-x-4">
                <Field
                  errorMessage={errorMessage}
                  fieldControl={(value, onChange, inError, onBlur) => (
                    <Input
                      className="w-40"
                      disabled={submitting}
                      inError={inError}
                      onBlur={onBlur}
                      onChange={onChange}
                      value={value}
                    />
                  )}
                  fieldName="code"
                  fieldValidations={{ required: contentData?.content.beErrFieldRequired }}
                  label={contentData?.content.beEpicPassIdLabel}
                />
                <ContinueButton
                  alignLeft
                  className="mt-2 justify-self-start"
                  disabled={submitting}
                  label={contentData?.content.beEpicPassSubmitBtn}
                  loading={submitting}
                />
              </div>
            </>
          )}
        </div>
      </form>
    </FormProvider>
  );
}
